import { isNullOrUndefined, versionCompare } from '../utils';

const ua = navigator.userAgent ?? '';

/**
 * 系统枚举
 */
export const ENUM_SYSTEM = {
  WINDOWS: 'windows',
  MAC: 'mac',
  Harmony: 'harmony',
  ANDROID: 'android',
  IOS: 'ios',
  IPAD: 'ipad',
  APAD: 'apad',
  LINUX: 'linux',
  OTHER: 'other',
};

/**
 * 系统检测正则
 */
export const SYSTEM_UA_REGEXPS = {
  [ENUM_SYSTEM.Harmony]: /Harmony/i,
  [ENUM_SYSTEM.IPAD]: /iPad/i,
  [ENUM_SYSTEM.IOS]: /iPh(one)?\sOS|iOS/i,
  [ENUM_SYSTEM.MAC]: /Macintosh|Mac\s?OS/i,
  [ENUM_SYSTEM.APAD]: /Android.+Pad/i,
  [ENUM_SYSTEM.ANDROID]: /Android|Adr\s|Linux\sU|U\sLinux/i,
  [ENUM_SYSTEM.WINDOWS]: /Windows/i,
  [ENUM_SYSTEM.LINUX]: /Linux/i,
};

/**
 * 检测系统类型
 */
export function detectSystem() {
  for (const system in SYSTEM_UA_REGEXPS) {
    if (SYSTEM_UA_REGEXPS[system].test(ua)) {
      return system;
    }
  }
  return ENUM_SYSTEM.OTHER;
}

/**
 * 检测App版本信息
 * https://ztwgw5unb5.feishu.cn/wiki/wikcnePHIXSyVEXqQHgdmZ4Q4xh?table=tbl83ks676VZ0eTI&view=vewLmiXuou
 */
export function detectApp() {
  const appInfo = {
    name: null,
    protocol: null,
    version: null,
    platform: null,
  };

  const appEngineMatches = /appEngine\/[\d.]*(.+?)\/(\d+\.\d+\.\d+)/i.exec(ua);
  if (appEngineMatches && appEngineMatches.length > 2) {
    // 引擎新版UA
    appInfo.name = appEngineMatches[1].toUpperCase();
    appInfo.protocol = '2023';
    appInfo.version = appEngineMatches[2];
  } else {
    // 旧版UA
    if (/(Tigerschool).*--(.+)$/i.test(ua)) {
      appInfo.name = 'TIGERSCHOOL';
    } else if (/(Korea).*--(.+)$/i.test(ua)) {
      appInfo.name = 'VIPKID KOREA';
    } else if (/vipkid_intl_parent_android/i.test(ua)) {
      appInfo.name = 'VIPKID PARENT';
      appInfo.platform = 'aphone';
    } else if (/vipkid_intl_parent_ios/i.test(ua)) {
      appInfo.name = 'VIPKID PARENT';
      appInfo.platform = 'iphone';
    } else if (/vipkid_intl_study_ipad/i.test(ua)) {
      appInfo.name = 'VIPKID STUDY';
      appInfo.platform = 'ipad';
    } else if (/VIPKID Study/i.test(ua)) {
      appInfo.name = 'VIPKID STUDY';
      appInfo.platform = 'apad';
    } else if (/vipkid_intl_fm_ios/i.test(ua)) {
      appInfo.name = 'VIPKID FM';
      appInfo.platform = 'iphone';
    } else if (/vipkidintl|pcstudent/i.test(ua)) {
      appInfo.name = 'VIPKID PC STUDY';
    }

    // 旧版不支持识别版本号和UA协议，固定写死
    if (appInfo.name) {
      appInfo.protocol = '2022';
      appInfo.version = '1.0.0';
    }
  }

  // 统一识别平台信息
  if (appInfo.name && appInfo.platform === null) {
    const platformMatches = /--(.+)$/i.exec(ua);
    if (platformMatches && platformMatches.length > 1) {
      appInfo.platform = platformMatches[1].toLowerCase();
    } else {
      appInfo.platform = detectSystem().toLowerCase();
    }
  }

  return appInfo;
}

/**
 * 浏览器枚举
 */
export const ENUM_BROWSER = {
  BOT: 'bot',
  CHROME: 'chrome',
  WEIXIN: 'weixin',
  NAVER: 'naver',
  KAKAO: 'kakao',
  QQ: 'qq',
  UC: 'uc',
  FIREFOX: 'firefox',
  OPERA: 'opera',
  IE: 'ie',
  EDGE: 'edge',
  SAFARI: 'safari',
  OTHER: 'other',
};

/**
 * 浏览器检测正则
 */
export const BROWSER_UA_REGEXPS = {
  [ENUM_BROWSER.WEIXIN]: /MicroMessenger|WECHAT/i,
  [ENUM_BROWSER.KAKAO]: /KAKAO/i,
  [ENUM_BROWSER.NAVER]: /NAVER/i,
  [ENUM_BROWSER.UC]: /UC(Web|Browser)/i,
  [ENUM_BROWSER.QQ]: /QQ\s|QQBrowser/i,
  [ENUM_BROWSER.BOT]: /Bot|Spider|Slurp|Crawler/i,
  [ENUM_BROWSER.EDGE]: /\sEdge?/i,
  [ENUM_BROWSER.CHROME]: /Chrome|CriOS/i,
  [ENUM_BROWSER.FIREFOX]: /Firefox/i,
  [ENUM_BROWSER.OPERA]: /(Opera|OPR\/)/i,
  [ENUM_BROWSER.IE]: /MSIE|IEMobile/i,
  [ENUM_BROWSER.SAFARI]: /Safari/i,
};

/**
 * 检测浏览器类型
 */
export function detectBrowser() {
  for (const browser in BROWSER_UA_REGEXPS) {
    if (BROWSER_UA_REGEXPS[browser].test(ua)) {
      return browser;
    }
  }
  return ENUM_BROWSER.OTHER;
}

export function ruleMatch(rules, target) {
  if (!isNullOrUndefined(target)) {
    if (!Array.isArray(rules)) {
      rules = [rules];
    }

    for (const rule of rules) {
      if (rule instanceof RegExp) {
        if (rule.test(target)) {
          return true;
        }
      } else if (String(rule).toLowerCase() === String(target).toLowerCase()) {
        return true;
      }
    }
  }

  return false;
}

function versionMoreThen(v1, v2) {
  const v = versionCompare(v1, v2);
  return v !== null && v > 0;
}

function versionMoreOrEqual(v1, v2) {
  const v = versionCompare(v1, v2);
  return v !== null && v >= 0;
}

export function versionMatch(versions, target) {
  if (!isNullOrUndefined(target)) {
    if (!Array.isArray(versions)) {
      versions = [versions];
    }

    for (const version of versions) {
      if (version instanceof RegExp) {
        if (version.test(target)) {
          return true;
        }
      } else {
        let versionMatchs = /([\d.]+)[~-]([\d.]+)/i.exec(version);
        if (versionMatchs && versionMatchs.length > 2) {
          if (versionMoreOrEqual(target, versionMatchs[1]) && versionMoreOrEqual(versionMatchs[2], target)) return true;
          continue;
        }
        versionMatchs = />=\s?([\d.]+)|([\d.]+)[~-]\*?/i.exec(version);
        if (versionMatchs && versionMatchs.length > 1) {
          if (versionMoreOrEqual(target, versionMatchs[1] ?? versionMatchs[2])) return true;
          continue;
        }
        versionMatchs = /<=\s?([\d.]+)|\*?[~-]([\d.]+)/i.exec(version);
        if (versionMatchs && versionMatchs.length > 1) {
          if (versionMoreOrEqual(versionMatchs[1] ?? versionMatchs[2], target)) return true;
          continue;
        }
        versionMatchs = />\s?([\d.]+)/i.exec(version);
        if (versionMatchs && versionMatchs.length > 1) {
          if (versionMoreThen(target, versionMatchs[1])) return true;
          continue;
        }
        versionMatchs = /<\s?([\d.]+)/i.exec(version);
        if (versionMatchs && versionMatchs.length > 1) {
          if (versionMoreThen(versionMatchs[1], target)) return true;
          continue;
        }
        if (version === target) return true;
      }
    }
  }

  return false;
}
